/**
 * OocQueue — admin out-of-catalog triage queue (STORY-010-004).
 *
 * Status filter (Segmented), newest-first table (cards on <640px), CSV export,
 * and a detail drawer with resolve / won't-do / reopen actions.
 *
 * Tokens: `--g-*` only. ≥44px tap targets.
 */
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button, GPill, Segmented, Skeleton } from "@garden/ui";
import { registerAdminI18n, ADMIN_NS } from "@/features/admin/api";
import { useOocList } from "@/features/admin/ooc/hooks";
import { exportOocCsv, type OocListItem } from "@/features/admin/ooc/api";
import { OocDrawer } from "@/features/admin/ooc/OocDrawer";

const STATUSES = ["in_progress", "resolved", "wont_do"] as const;

function statusTone(status: string): "warn" | "success" | "error" | "neutral" {
  switch (status) {
    case "in_progress":
      return "warn";
    case "resolved":
      return "success";
    case "wont_do":
      return "error";
    default:
      return "neutral";
  }
}

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function OocQueue(): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);

  const [status, setStatus] = useState<string>("in_progress");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading, isError, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useOocList(status);

  const items: OocListItem[] = data?.pages.flatMap((p) => p.items) ?? [];

  const handleExport = (): void => {
    void exportOocCsv(status).catch(() => toast.error(t("ooc.export.error")));
  };

  return (
    <div data-testid="ooc-queue-page" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-bold">{t("ooc.title")}</h1>
        <Button
          variant="secondary"
          size="sm"
          data-testid="ooc-export"
          onClick={handleExport}
        >
          {t("ooc.export.button")}
        </Button>
      </div>

      <div role="group" aria-label={t("ooc.filter.status")}>
        <Segmented
          options={STATUSES.map((s) => ({
            value: s,
            label: t(`ooc.status.${s}`),
          }))}
          value={status}
          onChange={(v) => setStatus(v)}
        />
      </div>

      {isLoading ? (
        <div>
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="mb-2 h-12 w-full rounded-lg" />
          ))}
        </div>
      ) : isError ? (
        <p className="text-sm text-[var(--g-error)]">{t("error.loadFailed")}</p>
      ) : items.length === 0 ? (
        <p data-testid="ooc-empty" className="py-8 text-center text-sm text-[var(--g-secondary)]">
          {t("ooc.empty")}
        </p>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden sm:block overflow-x-auto rounded-[var(--g-radius-md)] border border-[var(--g-hairline)]">
            <table data-testid="ooc-table" className="w-full min-w-[560px] border-collapse bg-[var(--g-surface)] text-sm">
              <thead className="bg-[var(--g-elevated)]">
                <tr>
                  <th scope="col" className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]">
                    {t("ooc.columns.createdAt")}
                  </th>
                  <th scope="col" className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]">
                    {t("ooc.columns.query")}
                  </th>
                  <th scope="col" className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]">
                    {t("ooc.columns.language")}
                  </th>
                  <th scope="col" className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]">
                    {t("ooc.columns.status")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr
                    key={item.id}
                    data-testid={`ooc-row-${item.id}`}
                    onClick={() => setSelectedId(item.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setSelectedId(item.id);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label={item.normalized_query}
                    className="cursor-pointer border-b border-[var(--g-hairline)] hover:bg-[var(--g-elevated)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--g-primary)]"
                  >
                    <td className="min-h-[44px] px-3 py-3 text-sm text-[var(--g-secondary)]">{formatDate(item.created_at)}</td>
                    <td className="px-3 py-3 text-sm font-medium">{item.normalized_query}</td>
                    <td className="px-3 py-3 text-sm text-[var(--g-secondary)]">{item.language}</td>
                    <td className="px-3 py-3">
                      <GPill tone={statusTone(item.status)} size="sm">
                        {t(`ooc.status.${item.status}`, { defaultValue: item.status })}
                      </GPill>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="flex flex-col gap-2 sm:hidden" data-testid="ooc-card-stack">
            {items.map((item) => (
              <div
                key={item.id}
                data-testid={`ooc-card-${item.id}`}
                onClick={() => setSelectedId(item.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setSelectedId(item.id);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={item.normalized_query}
                className="cursor-pointer rounded-[var(--g-radius)] border border-[var(--g-hairline)] bg-[var(--g-surface)] p-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="font-medium">{item.normalized_query}</div>
                  <GPill tone={statusTone(item.status)} size="sm">
                    {t(`ooc.status.${item.status}`, { defaultValue: item.status })}
                  </GPill>
                </div>
                <div className="mt-1 text-xs text-[var(--g-secondary)]">
                  {formatDate(item.created_at)} · {item.language}
                </div>
              </div>
            ))}
          </div>

          {hasNextPage ? (
            <Button
              variant="secondary"
              size="sm"
              className="mx-auto"
              disabled={isFetchingNextPage}
              onClick={() => void fetchNextPage()}
            >
              {isFetchingNextPage ? t("queue.refreshing") : t("queue.loadMore")}
            </Button>
          ) : null}
        </>
      )}

      <OocDrawer oocId={selectedId} onClose={() => setSelectedId(null)} />
    </div>
  );
}

export default OocQueue;
